import React, { useRef, useEffect, useState } from 'react';
import { useAppSelector } from '../../store/hooks';

interface CustomScrollbarProps {
  children: React.ReactNode;
  className?: string;
  maxHeight?: string;
}

const CustomScrollbar: React.FC<CustomScrollbarProps> = ({ 
  children, 
  className = '', 
  maxHeight = 'max-h-64' 
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);

  const updateScrollbar = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setScrollTop(scrollTop);
      setScrollHeight(scrollHeight);
      setClientHeight(clientHeight);
    }
  };

  useEffect(() => {
    updateScrollbar();
    
    const handleResize = () => updateScrollbar();
    const handleScroll = () => updateScrollbar();
    
    window.addEventListener('resize', handleResize);
    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleScrollbarClick = (e: React.MouseEvent) => {
    if (!contentRef.current || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const scrollbarHeight = rect.height;
    const scrollRatio = clickY / scrollbarHeight;
    const newScrollTop = scrollRatio * (scrollHeight - clientHeight);
    
    contentRef.current.scrollTop = newScrollTop;
  };

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart(e.clientY);
    setDragStartScrollTop(scrollTop);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !contentRef.current || !containerRef.current) return;
      
      const deltaY = e.clientY - dragStart;
      const scrollbarHeight = containerRef.current.getBoundingClientRect().height;
      const scrollRatio = deltaY / scrollbarHeight;
      const maxScroll = scrollHeight - clientHeight;
      const newScrollTop = Math.max(0, Math.min(maxScroll, dragStartScrollTop + scrollRatio * maxScroll));
      
      contentRef.current.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, dragStartScrollTop, scrollHeight, clientHeight]);

  const thumbHeight = clientHeight > 0 ? (clientHeight / scrollHeight) * clientHeight : 0;
  const thumbTop = clientHeight > 0 ? (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight) : 0;

  const scrollbarThumbColor = theme === 'dark' ? '#4b5563' : '#d1d5db';
  const scrollbarThumbHoverColor = theme === 'dark' ? '#6b7280' : '#9ca3af';

  return (
    <div className={`relative flex ${className}`}>
      {/* Content Area */}
      <div 
        ref={contentRef}
        className={`flex-1 overflow-y-auto ${maxHeight} custom-scrollbar-content`}
      >
        {children}
      </div>

      {/* Custom Scrollbar */}
      {scrollHeight > clientHeight && (
        <div 
          ref={containerRef}
          className={`w-2 ml-2 cursor-pointer ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-full`}
          onClick={handleScrollbarClick}
        >
          <div
            className={`w-full rounded-full transition-colors ${
              isDragging ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            }`}
            style={{
              height: `${thumbHeight}px`,
              transform: `translateY(${thumbTop}px)`,
              backgroundColor: scrollbarThumbColor,
            }}
            onMouseDown={handleThumbMouseDown}
            onMouseEnter={(e) => {
              if (!isDragging) {
                (e.target as HTMLElement).style.backgroundColor = scrollbarThumbHoverColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isDragging) {
                (e.target as HTMLElement).style.backgroundColor = scrollbarThumbColor;
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomScrollbar;
